// A cost billed right away and split by meter readings (finding Y15 of
// 19/10/2026): a reading measures what was used, so it belongs to whoever
// lived there, never to the days. The cost popup takes one reading per
// occupant, as the settlement's readings editor does: every tenant of the
// unit in the cost's period (from move-in to move-out) and the unit's
// vacant days. A vacant stretch is charged to the owner only with a reading
// of its own. Without readings per occupant (an older cost), a unit's one
// reading still goes over its tenants by their days.

import {
  daysInPeriod,
  mergedCoverage,
  OccupancyProration,
  occupantKey,
  overlapDays,
} from "./engine";
import { fromCents, splitCents, toCents } from "./money.utils";
import { ContractRef } from "./types";

export interface IReadingOccupant {
  // occupantKey.contract(id) or occupantKey.vacant(unitId).
  key: string;
  contractId?: string;
  // The tenant, or the vacancy label for the vacant days.
  label: string;
  from: string;
  to: string;
}

// The occupants of a unit in the period, oldest first, then its vacant days.
export const readingOccupantsOf = (params: {
  unitId: string;
  contracts: ContractRef[];
  period: { from: string; to: string };
  vacancyLabel: string;
}): IReadingOccupant[] => {
  const { unitId, contracts, period, vacancyLabel } = params;
  const own = contracts
    .filter(
      c =>
        String(c.unitId) === String(unitId) &&
        overlapDays(c.startDate, c.endDate, period.from, period.to) > 0,
    )
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const rows: IReadingOccupant[] = own.map(c => ({
    key: occupantKey.contract(c.id),
    contractId: c.id,
    label: c.tenantName,
    from: c.startDate > period.from ? c.startDate.slice(0, 10) : period.from,
    to: c.endDate < period.to ? c.endDate.slice(0, 10) : period.to,
  }));
  const { coveredDays, gaps } = mergedCoverage(own, period);
  if (coveredDays < daysInPeriod(period.from, period.to) && gaps.length > 0) {
    rows.push({
      key: occupantKey.vacant(String(unitId)),
      label: vacancyLabel,
      from: gaps[0].from,
      to: gaps[gaps.length - 1].to,
    });
  }
  return rows;
};

// A unit's reading: the sum of its occupants' readings, else (an older
// cost) the one reading of the unit.
export const unitReadingOf = (params: {
  unitId: string;
  occupants: IReadingOccupant[];
  values: Record<string, number>;
}): number => {
  const { unitId, occupants, values } = params;
  const own = occupants.filter(row => values[row.key] != null);
  if (own.length > 0) {
    return own.reduce((sum, row) => sum + (values[row.key] ?? 0), 0);
  }
  return values[unitId] ?? 0;
};

// A unit's part of the cost over its occupants by their readings, in whole
// cents: each tenant their own reading's part, the vacant days only their
// own reading's part (to the owner). Unchanged when no occupant has a
// reading.
export const prorationByReadings = (params: {
  proration: OccupancyProration;
  occupants: IReadingOccupant[];
  values: Record<string, number>;
}): OccupancyProration => {
  const { proration, occupants, values } = params;
  const weights = occupants.map(row => Math.max(0, values[row.key] ?? 0));
  if (!weights.some(weight => weight > 0)) return proration;
  const tenantCents = toCents(proration.tenantPortion);
  const parts = splitCents(tenantCents, weights);
  const partOf = (key: string) => {
    const index = occupants.findIndex(row => row.key === key);
    return index < 0 ? 0 : parts[index];
  };
  const shares = proration.shares.map(share => ({
    ...share,
    amount: fromCents(partOf(occupantKey.contract(share.contractId))),
  }));
  const vacantRow = occupants.find(row => !row.contractId);
  const vacancyCents = vacantRow ? partOf(vacantRow.key) : 0;
  const gapCents = splitCents(
    vacancyCents,
    proration.vacancyIntervals.map(gap => gap.days),
  );
  return {
    ...proration,
    shares,
    vacancyAmount: fromCents(vacancyCents),
    vacancyIntervals: proration.vacancyIntervals.map((gap, i) => ({
      ...gap,
      amount: fromCents(gapCents[i] ?? 0),
    })),
    ownerTotal: fromCents(toCents(proration.ownerBase) + vacancyCents),
  };
};

// The cost popup's split under readings (M30, retest of 2026-10-20): each
// occupant's row shows their own reading of the readings' total, and no
// days, since the days play no part. A unit whose occupants have no
// reading of their own (an older cost, one reading per unit) keeps its
// unit ratio and days: undefined.
export const readingRowShare = (params: {
  unitId: string;
  // The tenant's lease of the row; none for the unit's vacant days.
  contractId?: string;
  // The unit's leases in the cost's period.
  contractIds: string[];
  values: Record<string, number>;
  total: number;
}): number | undefined => {
  const { unitId, contractId, contractIds, values, total } = params;
  const keys = [
    ...contractIds.map(id => occupantKey.contract(id)),
    occupantKey.vacant(unitId),
  ];
  if (total <= 0 || !keys.some(key => values[key] != null)) return undefined;
  return (
    values[
      contractId ? occupantKey.contract(contractId) : occupantKey.vacant(unitId)
    ] ?? 0
  );
};
